import type { FC } from 'react';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Button, Header, Menu, MenuItem, MenuSection, MenuTrigger, Popover, Text } from '@launchpad-ui/components';
import type { IconName } from '@launchpad-ui/icons';
import { Icon } from '@launchpad-ui/icons';

import type { PermissionMode } from '../../../AgentInsightsContext';

interface PermissionModePickerProps {
  permissionMode: PermissionMode;
  githubIntegrated: boolean;
  disabled?: boolean;
  /** Icon shown to the left of the trigger label. */
  iconLeft?: IconName;
  onChange: (next: PermissionMode) => void;
}

export const PermissionModePicker: FC<PermissionModePickerProps> = ({
  permissionMode,
  githubIntegrated,
  disabled = false,
  iconLeft,
  onChange,
}) => {
  const buttonLabel = permissionMode === 'read-write' ? msg('vega.modePicker.agent') : msg('vega.modePicker.readOnly');

  return (
    <MenuTrigger>
      <Button variant="minimal" size="small" isDisabled={disabled}>
        {iconLeft ? <Icon name={iconLeft} size="small" /> : null}
        {buttonLabel}
        <Icon name="chevron-down" size="small" />
      </Button>
      <Popover placement="top start">
        <Menu
          aria-label={msg('vega.modePicker.selectAiMode')}
          selectionMode="single"
          selectedKeys={[permissionMode]}
          onAction={(key) => {
            if (key !== 'read' && key !== 'read-write') {
              return;
            }
            if (key !== permissionMode) {
              onChange(key);
            }
          }}
        >
          <MenuSection>
            <Header>
              <Text slot="label">
                <Message id="vega.modePicker.selectAiMode" />
              </Text>
            </Header>
            <MenuItem id="read" textValue={msg('vega.modePicker.readOnly')}>
              <Text slot="label">
                <Icon name="text-box-search" size="small" /> <Message id="vega.modePicker.readOnly" />
              </Text>
              <Text slot="description">
                <Message id="vega.modePicker.readOnlyDescription" />
              </Text>
            </MenuItem>
            <MenuItem id="read-write" textValue={msg('vega.modePicker.agent')}>
              <Text slot="label">
                <Icon name="graduation-cap" size="small" /> <Message id="vega.modePicker.agent" />
              </Text>
              <Text slot="description">
                <Message
                  id={
                    githubIntegrated ? 'vega.modePicker.agentDescription' : 'vega.modePicker.agentDescriptionNoGithub'
                  }
                />
              </Text>
            </MenuItem>
          </MenuSection>
        </Menu>
      </Popover>
    </MenuTrigger>
  );
};
